import { Form, Input, Modal, Select, TimePicker, message } from "antd";
import { AxiosError } from "axios";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import {
  ICreateMeetingSchedule,
  IMeetingRoom,
  IMeetingRoomQuery,
} from "../../../models/Hrm";
import {
  useCreateMeetingSchedule,
  useGetAllMeetingRoom,
} from "../../../query/HrmQuery";
import { notify } from "../../../utils/toast";
import { debounce } from "lodash";

const { RangePicker } = TimePicker;
const { Option } = Select;

interface IModalAddMeetingScheduleProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  initialDate?: Dayjs;
}

const ModalAddMeetingSchedule: React.FC<IModalAddMeetingScheduleProps> = ({
  open,
  onCancel,
  onSuccess,
  initialDate,
}) => {
  const createMeetingMutation = useCreateMeetingSchedule();
  const [form] = Form.useForm();
  const [filter, setFilter] = useState<IMeetingRoomQuery>({
    limit: 10,
    page: 1,
  });
  const { data: roomsResponse, isFetching } = useGetAllMeetingRoom(filter);

  const meetingRooms = roomsResponse?.data || [];
  useEffect(() => {
    if (open) {
      form.resetFields();
      const defaultStart = initialDate;
      const defaultEnd = initialDate ? initialDate.add(1, "hour") : undefined;
      form.setFieldsValue({
        timeRange:
          defaultStart && defaultEnd ? [defaultStart, defaultEnd] : undefined,
      });
      setFilter((prev) => ({
        ...prev,
        startTime: defaultStart?.toDate(),
        endTime: defaultEnd?.toDate(),
        page: 1,
      }));
    }
  }, [open, initialDate, form]);

  const onFinish = async (values: any) => {
    try {
      const [start, end] = values.timeRange;
      const targetDate = initialDate || dayjs();

      const payload: ICreateMeetingSchedule = {
        title: values.title,
        startTime: targetDate
          .hour(start.hour())
          .minute(start.minute())
          .second(0)
          .millisecond(0)
          .toISOString(),
        endTime: targetDate
          .hour(end.hour())
          .minute(end.minute())
          .second(0)
          .millisecond(0)
          .toISOString(),
        roomId: values.meetingRoomId,
      };

      await createMeetingMutation.mutateAsync(payload);
      notify.success("Tạo lịch họp thành công");
      onSuccess();
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      const msg = err.response?.data?.message || "Tạo lịch họp thất bại";
      message.error(msg);
    }
  };
  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof IMeetingRoomQuery, value?: string) => {
        setFilter((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [setFilter],
  );

  const handleSearch = (field: keyof IMeetingRoomQuery) => (value?: string) => {
    debouncedSearch(field, value);
  };
  return (
    <Modal
      title="Tạo lịch họp mới"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={createMeetingMutation.isPending}
      destroyOnClose
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="title"
          label="Tiêu đề cuộc họp"
          rules={[{ required: true, message: "Vui lòng nhập tiêu đề" }]}
        >
          <Input placeholder="Ví dụ: Họp giao ban tuần" />
        </Form.Item>

        <Form.Item
          name="timeRange"
          label="Thời gian"
          rules={[{ required: true, message: "Vui lòng chọn thời gian" }]}
        >
          <RangePicker
            format="HH:mm"
            style={{ width: "100%" }}
            placement="bottomLeft"
            getPopupContainer={(triggerNode) =>
              triggerNode.parentNode as HTMLElement
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                const targetDate = initialDate || dayjs();
                setFilter((prev) => ({
                  ...prev,
                  startTime: targetDate
                    .hour(dates[0]!.hour())
                    .minute(dates[0]!.minute())
                    .second(0)
                    .millisecond(0)
                    .toDate(),
                  endTime: targetDate
                    .hour(dates[1]!.hour())
                    .minute(dates[1]!.minute())
                    .second(0)
                    .millisecond(0)
                    .toDate(),
                  page: 1,
                }));
              } else {
                setFilter((prev) => ({
                  ...prev,
                  startTime: undefined,
                  endTime: undefined,
                  page: 1,
                }));
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="meetingRoomId"
          label="Phòng họp"
          dependencies={["timeRange"]}
          rules={[{ required: true, message: "Vui lòng chọn phòng họp" }]}
        >
          <Select
            placeholder="Chọn phòng họp"
            showSearch
            filterOption={false}
            onSearch={handleSearch("name")}
            onPopupScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                Math.round(target.scrollTop + target.offsetHeight) >=
                  target.scrollHeight - 10 &&
                !isFetching
              ) {
                if (
                  roomsResponse?.pagination &&
                  (filter.page || 1) * (filter.limit || 10) <
                    roomsResponse.pagination.total
                ) {
                  setFilter((prev: any) => ({
                    ...prev,
                    page: (prev.page || 1) + 1,
                  }));
                }
              }
            }}
          >
            {meetingRooms.map((room: IMeetingRoom) => (
              <Option key={room.id} value={room.id}>
                {room.name} - {room.floor} - {room.location}{" "}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default React.memo(ModalAddMeetingSchedule);
